// ============================================================
// targetGear - what the Target Gear tab shows, as pure answers
// ============================================================
// The armour rows (current AP, the original it was edited from, and the
// effective AP a hit meets) and the reordered weapon list. No React.

import type { Target } from '../types';
import { isLocCoveredForBody, type BodyConfig } from '../engine/bodyConfig';

/** One armour table row. */
export interface ArmourRow {
  current: number;
  /** The AP before any in-combat edit (the current AP when there is no record). */
  orig: number;
  /** Edited in combat away from its original. */
  changed: boolean;
  /** Base + natural armour + machine + cover at this location. */
  effectiveAP: number;
}

const VEHICLE_FACINGS = [
  { id: 'front', label: 'Front' },
  { id: 'side', label: 'Side' },
  { id: 'rear', label: 'Rear' },
];

/** The armour table's locations: a vehicle's three facings, else the body config's locations. */
export function armourLocations(isVehicle: boolean, bodyConfig: BodyConfig): { id: string; label: string }[] {
  if (isVehicle) return VEHICLE_FACINGS;
  return bodyConfig.locations.map((loc) => ({ id: loc.id, label: loc.name }));
}

/** The cover AP at a location: only a real cover level that covers it counts. */
function coverAPAt(target: Target, id: string, bodyConfig: BodyConfig): number {
  const cover = target.cover;
  if (!cover || cover.level === 'none') return 0;
  return isLocCoveredForBody(cover.level, id, bodyConfig) ? cover.ap : 0;
}

/** The numbers behind one armour row. */
export function armourRow(target: Target, id: string, bodyConfig: BodyConfig): ArmourRow {
  const current = (target.armour || {})[id] || 0;
  const original = target.armourOriginal;
  const orig = original ? original[id] || 0 : current;
  const extra = (target.naturalArmour || 0) + (target.machineAP || 0) + coverAPAt(target, id, bodyConfig);
  return { current, orig, changed: original != null && current !== orig, effectiveAP: current + extra };
}

/** A copy of `list` with the items at `from` and `to` swapped. */
export function swapped<T>(list: T[], from: number, to: number): T[] {
  const next = [...list];
  [next[from], next[to]] = [next[to], next[from]];
  return next;
}
